/** Locale-correct currency formatting for the taal-neutral numeric amounts from the BFF. */
export function formatInvoiceAmount(amount: number, currency: string, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);
}

/** Locale-correct date formatting for the ISO dates (YYYY-MM-DD) returned by the BFF. */
export function formatInvoiceDate(isoDate: string, locale: string): string {
  return new Date(isoDate).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * h12-0 contract §1.3: an invoice with no ar_items match ('unknown' status) never claims
 * "paid" — but a neutral, non-lying hint can still be derived from the due date: a passed
 * due date renders as "open — due date passed" instead of a bare "unknown" badge.
 */
export function isDueDatePassed(dueDate: string | null): boolean {
  if (!dueDate) return false;
  const due = new Date(`${dueDate}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return due.getTime() < today.getTime();
}

/**
 * The BFF only carries `period` as a bare month number (1-12) — the invoice is typically
 * raised the month AFTER the serviced period, so the period's year is derived from the
 * invoice date: when the period month is LATER than the invoice month, the period falls in
 * the previous calendar year (e.g. invoiced 2 Jan 2026 for period December -> 2025).
 */
export function formatInvoicePeriod(period: number, invoiceDate: string | null, locale: string): string {
  if (!Number.isInteger(period) || period < 1 || period > 12) return String(period);
  if (!invoiceDate) return new Date(Date.UTC(2000, period - 1, 1)).toLocaleDateString(locale, { month: 'long' });

  const invoiced = new Date(invoiceDate);
  const invoicedMonth = invoiced.getUTCMonth() + 1;
  const invoicedYear = invoiced.getUTCFullYear();
  const year = period > invoicedMonth ? invoicedYear - 1 : invoicedYear;

  return new Date(Date.UTC(year, period - 1, 1)).toLocaleDateString(locale, { month: 'long', year: 'numeric' });
}
